/**
 * Undo / Redo / Save icon trio (#2289).
 *
 * Lives in the inner body-card header right above the editor content,
 * so the edit-history actions sit next to what they affect instead of
 * in the top EditorHeader cluster. Each editor imports this and places
 * it on the LEFT of its inner header; the formatting toolbox stays on
 * the right.
 *
 * This component is deliberately editor-agnostic: it takes the
 * callbacks + disabled/loading booleans and renders a tight pill-free
 * icon row. Callers decide whether to render it at all (e.g. the
 * split-view source pane doesn't — read-only).
 */

import { Redo2, Save, Undo2 } from 'lucide-react';
import cn from 'classnames';

import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

interface Props {
  undo: () => void;
  redo: () => void;
  save: () => void;
  undoDisabled?: boolean;
  redoDisabled?: boolean;
  saveDisabled?: boolean;
  saving?: boolean;
  undoLabel: string;
  redoLabel: string;
  saveLabel: string;
}

export function EditorHistoryTools({
  undo,
  redo,
  save,
  undoDisabled,
  redoDisabled,
  saveDisabled,
  saving,
  undoLabel,
  redoLabel,
  saveLabel
}: Props) {
  const { getColour } = useThemeContext();

  const buttonClass = cn(
    'inline-flex items-center justify-center h-8 w-8 rounded-full border transition-colors',
    'hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40'
  );
  const buttonStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('border'),
    color: getColour('icon')
  };

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Tooltip label={shortcutLabel(undoLabel, 'Ctrl+Z')}>
        <button
          type="button"
          onClick={undo}
          disabled={undoDisabled}
          aria-label={undoLabel}
          className={buttonClass}
          style={buttonStyle}
        >
          <Undo2 size={14} aria-hidden="true" />
        </button>
      </Tooltip>
      <Tooltip label={shortcutLabel(redoLabel, 'Ctrl+Y')}>
        <button
          type="button"
          onClick={redo}
          disabled={redoDisabled}
          aria-label={redoLabel}
          className={buttonClass}
          style={buttonStyle}
        >
          <Redo2 size={14} aria-hidden="true" />
        </button>
      </Tooltip>
      <Tooltip label={shortcutLabel(saveLabel, 'Ctrl+S')}>
        <button
          type="button"
          onClick={save}
          disabled={saveDisabled || saving}
          aria-label={saveLabel}
          className={buttonClass}
          style={buttonStyle}
        >
          <Save size={14} aria-hidden="true" />
        </button>
      </Tooltip>
    </div>
  );
}
